import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { DEFAULT_LOCALE, isLocale, type Locale } from '@hub/contracts'
import { i18n } from './index'

/** localStorage key — the choice survives logout and applies on the login page. */
export const LOCALE_STORAGE_KEY = 'hub-locale'

interface LocaleContextValue {
  locale: Locale
  /** Switch the UI language (client side only — persisting to the profile is the caller's job). */
  setLocale: (next: Locale) => void
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

function readStoredLocale(): Locale | null {
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    return isLocale(raw) ? raw : null
  } catch {
    return null
  }
}

function applyLocale(locale: Locale): void {
  if (i18n.language !== locale) void i18n.changeLanguage(locale)
  document.documentElement.lang = locale
}

/** Initial language: stored choice, else Swedish (the product default). */
export function initialLocale(): Locale {
  return readStoredLocale() ?? DEFAULT_LOCALE
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const initial = initialLocale()
    applyLocale(initial)
    return initial
  })

  useEffect(() => {
    applyLocale(locale)
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next)
    } catch {
      // ignore — the choice still applies for this session
    }
  }, [])

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale])
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useLocale must be used inside <LocaleProvider>')
  return ctx
}
